import useTranslator from "../../hooks/useTranslator";
import {
  convertCurrencyFormat,
  detectIdentityType,
  haveValue,
  isDefaulter,
  selectPreferredServiceFile,
} from "../../helper/common";
import { useMemo, useState } from "react";
import moment from "moment";
import { Tooltip as ReactTooltip } from "react-tooltip";
import constants from "../../Utils/constants";
import useServiceFile from "../../hooks/useServiceFile";
import { navigateToTenantDocument } from "../../Utils/tenantDocumentNavigation";
import { Badge, Button, Card, Drawer, Icon, InfoList, Notice } from "../design";
import { ReportSectionTitle, ReportToggle } from "./ReportDrawerParts";
import "./reportDrawer.css";

const formatDebtDate = (d) => `${d?.DD}/${d?.MM}/${d?.AAAA}`;

const TOOLTIP_CLASS = "react-tooltip-styling details-skor-tooltip";

// ─── Single debt block dropdown ───────────────────────────────────────────────
const DebtDetailDropdown = ({ block, index, _t, experianJson }) => {
  const [showDropdown, setShowDropDown] = useState(false);

  const tooltipSuffix = `-${index}`;
  const statusCodes = block?.SituacionPago?.TTipoCodigo;

  return (
    <Card as="div" className="reportDrawer__card">
      <ReportToggle
        open={showDropdown}
        onClick={() => setShowDropDown(!showDropdown)}
      >
        <div className="reportDrawer__debt-head">
          <div>
            <h3 className="reportDrawer__debt-title">
              {block?.Informante && block.Informante}
            </h3>
            <p className="reportDrawer__debt-date">
              {_t("lastDefault")}:{" "}
              {formatDebtDate(block?.FechaUltimoVencimientoImpagado)}
            </p>
          </div>
          <div className="reportDrawer__debt-meta">
            <Badge tone="danger">{_t(statusCodes?.[0]?.Descripcion)}</Badge>
            <span>€{block?.SaldoImpagado}</span>
          </div>
        </div>
      </ReportToggle>

      {showDropdown && (
        <div className="reportDrawer__body">
          {/* Operation Details */}
          <section>
            <ReportSectionTitle
              icon="fileText"
              tooltipId={`operation-details-tooltip${tooltipSuffix}`}
            >
              {_t("operationDetails")}
            </ReportSectionTitle>
            <ReactTooltip
              id={`operation-details-tooltip${tooltipSuffix}`}
              noArrow
              className={TOOLTIP_CLASS}
              render={() => (
                <p>
                  {_t(
                    "itIdentifiesTheLoanBehindTheDebtShowingWhoIssuedItWhatTypeOfCreditItIsAndTheOfficialOperationIdUsedByTheLenderWhenReportingItToTheCreditBureau"
                  )}
                </p>
              )}
            />
            <InfoList
              items={[
                { label: _t("creditor"), value: block?.Informante },
                {
                  label: _t("product"),
                  value: block?.TipoProductoFinanciado?.Descripcion,
                },
                { label: _t("operationId"), value: block?.IdOperacion },
              ]}
            />
          </section>

          {/* Payment History */}
          <section>
            <ReportSectionTitle
              icon="calendar"
              tooltipId={`payment-history-tooltip${tooltipSuffix}`}
            >
              {_t("paymentHistory")}
            </ReportSectionTitle>
            <ReactTooltip
              id={`payment-history-tooltip${tooltipSuffix}`}
              noArrow
              className={TOOLTIP_CLASS}
              render={() => (
                <p>
                  {_t(
                    "itSummarisesHowTheDebtHasEvolvedOverTimeWhenTheTenantFirstStoppedPayingHowManyInstallmentsRemainUnpaidHowMuchIsStillOwedAndTheMostRecentStatusReportedByTheLender"
                  )}
                </p>
              )}
            />
            <InfoList
              items={[
                {
                  label: _t("firstDefault"),
                  value: formatDebtDate(block?.FechaPrimerVencimientoImpagado),
                },
                {
                  label: _t("lastDefault"),
                  value: formatDebtDate(block?.FechaUltimoVencimientoImpagado),
                },
                {
                  label: _t("unpaidInstallments"),
                  value: block?.NumeroCuotasImpagadas,
                },
                {
                  label: _t("balanceOutstanding"),
                  value: `€${block?.SaldoImpagado}`,
                },
                {
                  label: _t("maximumOutstandingAmount"),
                  value: `€${block?.MaximoImporteImpagado}`,
                },
                {
                  label: _t("debtStatus"),
                  value: (
                    <Badge tone="danger">
                      {_t(statusCodes?.[0]?.Descripcion)}
                    </Badge>
                  ),
                },
              ]}
            />
          </section>

          {/* Payment Status Evolution */}
          <section>
            <ReportSectionTitle
              icon="swap"
              tooltipId={`payment-status-tooltip${tooltipSuffix}`}
            >
              {_t("paymentStatusEvolution")}
            </ReportSectionTitle>
            <ReactTooltip
              id={`payment-status-tooltip${tooltipSuffix}`}
              noArrow
              className={TOOLTIP_CLASS}
              render={() => (
                <p>
                  {_t(
                    "thisTimelineShowsHowTheDebtProgressedFromEarlyLatePayments60150DaysWhichIndicateSeveralMissedInstallmentsToAJudicialStatusMeaningTheCaseHasEscalatedToFormalLegalAction"
                  )}
                </p>
              )}
            />
            <ul className="reportDrawer__steps">
              {[...(statusCodes || [])]?.reverse()?.map((val) => {
                if (["2", "3", "4"].includes(val?.Codigo))
                  return (
                    <li key={val?.Codigo} className="reportDrawer__step">
                      <Badge tone="neutral">{val?.Codigo}</Badge>
                      <span>{_t(val?.Descripcion)}</span>
                    </li>
                  );
                return null;
              })}
              {haveValue(statusCodes?.[0]) && (
                <li className="reportDrawer__step">
                  <Badge tone="danger">
                    {[...(statusCodes || [])]?.reverse()?.pop()?.Codigo}
                  </Badge>
                  <span>
                    {[...(statusCodes || [])]?.reverse()?.pop()?.Descripcion}
                  </span>
                </li>
              )}
            </ul>
          </section>

          {/* Registration Details */}
          <section>
            <ReportSectionTitle
              icon="calendar"
              tooltipId={`registration-details-tooltip${tooltipSuffix}`}
            >
              {_t("registrationDetails")}
            </ReportSectionTitle>
            <ReactTooltip
              id={`registration-details-tooltip${tooltipSuffix}`}
              noArrow
              className={TOOLTIP_CLASS}
              render={() => (
                <p>
                  {_t(
                    "theRegistrationDateShowsWhenTheUnpaidDebtWasFirstreportedToTheCreditBureauAndTheLastUpdateReflectsTheMostRecentInformationProvidedByTheLender"
                  )}
                </p>
              )}
            />
            <InfoList
              items={[
                {
                  label: _t("registrationDate"),
                  value: formatDebtDate(block?.FechaAlta),
                },
                {
                  label: _t("lastUpdate"),
                  value: formatDebtDate(block?.FechaUltimaActualizacion),
                },
              ]}
            />
          </section>

          {/* Sociodemographic Mark */}
          <section>
            <ReportSectionTitle
              icon="home"
              tooltipId={`socio-demo-tooltip${tooltipSuffix}`}
            >
              {_t("SociodemographicMark")}
            </ReportSectionTitle>
            <ReactTooltip
              id={`socio-demo-tooltip${tooltipSuffix}`}
              noArrow
              className={TOOLTIP_CLASS}
              render={() => (
                <p>
                  {_t(
                    "thisRatingComesFromNeighborhoodDemographicsNotTheTenantPersonalDataAreasScoredATendToShowLowerAverageRiskWhileFAreasShowHigherRiskThoughThisStillDoesntDescribeTheTenantThemselves"
                  )}
                </p>
              )}
            />
            <p className="reportDrawer__note">
              {
                experianJson?.["s:Envelope"]?.["s:Body"]?.GenerarInformeResponse
                  ?.GenerarInformeResult?.InformeDelphi4A3?.InformacionDelphi
                  ?.Nota
              }
              <span className="reportDrawer__note-sub">
                ({_t("basedOnPostalCode")} {block?.Direccion?.CodigoPostal})
              </span>
            </p>
          </section>
        </div>
      )}
    </Card>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────
const DefaultCheckReportModal = ({
  isOpen,
  onClose,
  user,
  serviceFile = null,
  isGuarantor = false,
}) => {
  const { _t } = useTranslator();
  const experianJson = user?.tenant_extra_field_id?.experianJson;
  const experianData =
    experianJson?.["s:Envelope"]?.["s:Body"]?.GenerarInformeResponse
      ?.GenerarInformeResult?.InformeDelphi4A3;

  const TBloqueDetalleOperacionRaw =
    experianJson?.badexcugRawData?.["s:Envelope"]?.["s:Body"]
      ?.GenerarInformeResponse?.GenerarInformeResult?.InformeDetalleOperaciones
      ?.Registros?.TBloqueDetalleOperacion;

  // Normalise: always work with an array
  const TBloqueDetalleOperacionList = Array.isArray(TBloqueDetalleOperacionRaw)
    ? TBloqueDetalleOperacionRaw
    : TBloqueDetalleOperacionRaw
      ? [TBloqueDetalleOperacionRaw]
      : [];

  // For the summary card at the top, use the first block (worst/most relevant)
  const primaryBlock = TBloqueDetalleOperacionList[0];

  const isUserDefaulter = isDefaulter(experianData);
  const experianStatus = user?.tenant_extra_field_id?.experian?.status;
  // Identity doc used for this Experian run (TenantServiceFile, EXPERIAN).
  // The parent prefetches it on profile load (cached in the hook), so the
  // modal's own call below resolves from cache instead of fetching on open.
  const fetchedExperianFile = useServiceFile(
    user?._id,
    "EXPERIAN",
    isOpen,
    isGuarantor
  );
  const experianServiceFile = useMemo(
    () => selectPreferredServiceFile(serviceFile ?? fetchedExperianFile),
    [serviceFile, fetchedExperianFile]
  );
  // Doc type/number come only from this Experian run's service file — no
  // fallbacks to the Onfido report or profile. If the type is missing, the
  // row is hidden entirely.
  const experianIdentityNumber =
    [
      experianServiceFile?.metadata?.dni_nie,
      experianServiceFile?.metadata?.id_number,
      experianServiceFile?.metadata?.passport_id,
    ].find((value) => haveValue(value)) ?? "";
  const rawExperianDocType =
    experianServiceFile?.metadata?.document_type ??
    (haveValue(experianServiceFile?.metadata?.dni_nie) ? "dni_nie" : null) ??
    // The EXPERIAN record may carry no document_type while holding a DNI in
    // id_number (identitySource onfido_personal_number) alongside a stale
    // passport_id — detect DNI/NIE from the number format before falling
    // back to passport.
    (["dni", "nie"].includes(detectIdentityType(experianIdentityNumber))
      ? "dni_nie"
      : null) ??
    (haveValue(experianServiceFile?.metadata?.passport_id)
      ? "passport"
      : null) ??
    "";
  const identityTypeLabel = !haveValue(rawExperianDocType)
    ? ""
    : ["dni_nie", "dni", "nie"].includes(rawExperianDocType)
      ? "DNI"
      : String(rawExperianDocType)
          .replace(/_/g, " ")
          .replace(/\b\w/g, (letter) => letter.toUpperCase());
  const experianIdentitySource =
    haveValue(identityTypeLabel) && haveValue(experianIdentityNumber)
      ? `${identityTypeLabel} ··${String(experianIdentityNumber).slice(-4)}`
      : identityTypeLabel;
  const experianFileDate = experianServiceFile?.created_at
    ? moment(experianServiceFile.created_at)
    : null;
  const hasExperianFileDate = Boolean(experianFileDate?.isValid());
  const checkedWithLabel = _t("checkedWith");
  const onDateLabel = _t("on");
  const normalizedExperianStatus = experianStatus?.toLowerCase();
  // Experian commented condition
  const isExperianReportMissing = !haveValue(experianJson);
  // const isExperianReportMissing = false;

  if (isExperianReportMissing) {
    const isPending = [constants.PENDING, constants.PROCESSING].includes(
      normalizedExperianStatus
    );
    const isProcessing = normalizedExperianStatus === constants.PROCESSING;
    const isVerifiedDocumentMissing =
      normalizedExperianStatus === constants.VERIFIED_DOCUMENT_NOT_FOUND;
    const statusText = isProcessing
      ? _t("processing", "Processing")
      : isPending
        ? _t("pending", "Pending")
        : _t("checkNotAvailable", "Check not available");
    const description = isVerifiedDocumentMissing
      ? _t(
          "defaultCheckUnavailableNoVerifiedId",
          "We couldn't run the default check because this tenant has no verified ID yet."
        )
      : isPending
        ? _t(
            "defaultCheckPendingDescription",
            "The default check is waiting for the tenant's verified identity information."
          )
        : _t(
            "defaultCheckUnavailableDescription",
            "We couldn't complete the default check for this tenant."
          );
    const action = isVerifiedDocumentMissing
      ? _t(
          "askTenantToUploadValidIdForDefaultCheck",
          "Ask the tenant to upload a valid ID document (DNI, NIE or passport) so the check can run."
        )
      : isPending
        ? _t(
            "defaultCheckPendingAction",
            "The report will be available when identity verification and the default check are complete."
          )
        : _t(
            "defaultCheckUnavailableAction",
            "Please try again later or contact support if the problem continues."
          );

    return (
      <Drawer
        open={isOpen}
        onClose={onClose}
        title={_t("defaulterCheckReport")}
        closeLabel={_t("close")}
      >
        <div className="reportDrawer__stack">
          <Notice tone={isPending ? "info" : "warning"} title={statusText}>
            {description}
          </Notice>
          <Notice tone="info">{action}</Notice>
        </div>
      </Drawer>
    );
  }

  const getMaxDelay = () => {
    let maxDays = 0;
    let maxDescripcion = "";

    const toDate = (obj) => {
      if (!obj?.DD || !obj?.MM || !obj?.AAAA) return null;
      return new Date(
        `${obj.AAAA}-${String(obj.MM).padStart(2, "0")}-${String(obj.DD).padStart(2, "0")}`
      );
    };

    TBloqueDetalleOperacionList.forEach((block) => {
      const first = toDate(block?.FechaPrimerVencimientoImpagado);
      const last = toDate(block?.FechaUltimoVencimientoImpagado);

      if (first && last) {
        const days = Math.round((last - first) / (1000 * 60 * 60 * 24));

        if (days > maxDays) {
          maxDays = days;
          maxDescripcion =
            block?.SituacionPago?.TTipoCodigo?.[0]?.Descripcion ?? "";
        }
      }
    });

    return {
      maxDays,
      maxDescripcion,
    };
  };

  const { maxDescripcion } = getMaxDelay();

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={_t("defaulterCheckReport")}
      closeLabel={_t("close")}
    >
      <div className="reportDrawer__stack">
        {/* ── Summary risk card ── */}
        <Notice
          tone={isUserDefaulter ? "danger" : "success"}
          title={
            isUserDefaulter ? _t("defaultsDetected") : _t("noDefaultsDetected")
          }
        >
          <div className="reportDrawer__lines">
            {haveValue(identityTypeLabel) && (
              <div className="reportDrawer__source">
                <Icon name="idCard" size={16} />
                <span>{checkedWithLabel}</span>
                {experianServiceFile ? (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => {
                      navigateToTenantDocument({
                        tenantId: user?._id,
                        isGuarantor,
                        service: "EXPERIAN",
                        serviceFile: experianServiceFile,
                      });
                      onClose?.();
                    }}
                  >
                    {experianIdentitySource} ›
                  </Button>
                ) : (
                  <span>{experianIdentitySource}</span>
                )}
                {hasExperianFileDate && (
                  <span>
                    · {onDateLabel} {experianFileDate.format("DD-MM-YYYY")}
                  </span>
                )}
              </div>
            )}

            {isUserDefaulter && TBloqueDetalleOperacionList.length > 0 && (
              <>
                <InfoList
                  items={[
                    {
                      label: _t("totalDebt"),
                      value: `€${convertCurrencyFormat(
                        TBloqueDetalleOperacionList.reduce((sum, block) => {
                          const val = parseFloat(
                            String(block?.SaldoImpagado ?? "0").replace(
                              ",",
                              "."
                            )
                          );
                          return sum + (isNaN(val) ? 0 : val);
                        }, 0).toFixed(2)
                      )}`,
                    },
                    ...(maxDescripcion
                      ? [
                          {
                            label: _t("longerDelay"),
                            value: _t(maxDescripcion),
                          },
                        ]
                      : []),
                    {
                      label: _t("lastMissedPayment"),
                      value: (() => {
                        let latestDate = null;
                        let latestStr = "";
                        TBloqueDetalleOperacionList.forEach((block) => {
                          const obj = block?.FechaUltimoVencimientoImpagado;
                          if (!obj?.DD || !obj?.MM || !obj?.AAAA) return;
                          const d = new Date(
                            `${obj.AAAA}-${String(obj.MM).padStart(2, "0")}-${String(obj.DD).padStart(2, "0")}`
                          );
                          if (!latestDate || d > latestDate) {
                            latestDate = d;
                            latestStr = `${obj.DD}/${obj.MM}/${obj.AAAA}`;
                          }
                        });
                        return latestStr;
                      })(),
                    },
                  ]}
                />
                <span>
                  {TBloqueDetalleOperacionList.length} {_t("recordsFound")}
                </span>
              </>
            )}
          </div>
        </Notice>

        {/* ── Debt detail dropdowns (one per block) ── */}
        {isUserDefaulter ? (
          TBloqueDetalleOperacionList.map((block, index) => (
            <DebtDetailDropdown
              key={block?.IdOperacion ?? index}
              block={block}
              index={index}
              _t={_t}
              experianJson={experianJson}
            />
          ))
        ) : (
          <Notice tone="info">{_t("thisUserHasNoDefaultHistory")}</Notice>
        )}
      </div>
    </Drawer>
  );
};

export default DefaultCheckReportModal;
